import { AppConstants } from 'cd-eft/appConstants';
import { IDisposable } from 'cd-eft/framework/Disposer';
import { Geometry, GraphicsLayer, MapView, Point, Polyline, SketchViewModel, SpatialReference, esriGeometryEngine, esriProjection } from 'cd-eft/utils/esriTypes';
import { action, IReactionDisposer, reaction } from 'mobx';
import { XyzPoint } from '../../SurveyEngineering/XyzPoint';
import { DynamicProfileLayerType, ProfileKeyCode } from './DynamicProfileEnums';
import { DynamicProfileHubBase } from './DynamicProfileHubBase';
import { MapTooltipProvider } from './MapTooltipProvider';
import { MapViewHighlightSuspender } from './MapViewHighlightSuspender';
import { appConfig } from 'cd-eft/config/AppConfig';
import stores from '..';
import { getDistance2D } from "cd-eft/components/SurveyEngineering/Maps/SETMapFunctions/elevationInterpolationEngine";

const projectsLibraryStore = stores.AppStore.projectsLibraryStore;
abstract class DynamicMapStoreBase implements IDisposable {
  protected hub: DynamicProfileHubBase | undefined;

  protected mapView: MapView | undefined = void 0;
  private _sketchLayer: GraphicsLayer | undefined = void 0;
  private _curveLayer: GraphicsLayer | undefined = void 0;
  protected _mapTooltipProvider: MapTooltipProvider | undefined = void 0;
  protected _highlightSuspender: MapViewHighlightSuspender | undefined = void 0;

  protected mainSketchVM: SketchViewModel | undefined = void 0;
  public mapSpatialReference: SpatialReference | undefined = void 0;
  public dataSpatialReference: SpatialReference | undefined = void 0;
  private deleteVertexReactionDisposer: IReactionDisposer;

  protected profileKeys: ProfileKeyCode[] = [ProfileKeyCode.DEFAULT];

  protected emptyPromiseVoid: Promise<void> = new Promise<void>(() => { });

  private _currentWkid = appConfig.tempHardCodes.wkid;
  protected originalProfilePoints: any[] = []; // user clicks plus contour intersecitons
  public activeProfilePoints: any[] = [];
  protected userOriginalPoints: any[] = []; // user clicks only
  public userActivePoints: any[] = [];

  constructor() {
    // console.log('new DesignProfileMapStore.');
    // this.hub = hub;

    this.deleteVertexReactionDisposer = reaction(
      () => this.hub?.TableBorneVertexIndexToDeleteInfo,
      async (info) => {
        if (info) await this.deleteVertex(info);
      }
    );
  }

  @action
  protected async deleteVertex(info: any) {
    this.mainSketchVM?.complete();
    if (this.sketchLayer.graphics && this.sketchLayer.graphics.length > 0) {
      const currentLine = this.sketchLayer.graphics.getItemAt(0).geometry as Polyline;
      currentLine.removePoint(0, info.index);
      const newLine = new Polyline({
        paths: currentLine.paths,
        spatialReference: currentLine.spatialReference,
      });
      this.sketchLayer.graphics.getItemAt(0).geometry = newLine;
    }
  }

  set MapView(mapView: MapView) {
    this.mapView = mapView;

    this.mapSpatialReference = this.mapView?.spatialReference;
    this.dataSpatialReference = projectsLibraryStore.projectSpatialReference;

    this.setUpSketchCapability();
    this._mapTooltipProvider = new MapTooltipProvider(this.mapView, projectsLibraryStore.projectSpatialReference!);

    if (mapView) {
      // console.log('MapView highlight suspended.');

      this._highlightSuspender = new MapViewHighlightSuspender(this.mapView);
      this._highlightSuspender.start();
    }
  }

  get MapView(): MapView {
    return this.mapView!;
  }

  protected get sketchLayer() {
    const id = 'alignment_sketch';
    const title = 'Alignment Edited';

    if (!this._sketchLayer) {
      const layer = this.mapView?.map.findLayerById(id);
      if (layer) {
        this._sketchLayer = layer as GraphicsLayer;
      } else {
        this._sketchLayer = new GraphicsLayer({ id, title });
        this.mapView?.map.add(this._sketchLayer);
      }
    }

    return this._sketchLayer;
  }

  protected get curveLayer() {
    const id = 'alignment_curved';
    const title = 'Curved Alignment';

    if (!this._curveLayer) {
      const layer = this.mapView?.map.findLayerById(id);
      if (layer) {
        this._curveLayer = layer as GraphicsLayer;
      } else {
        this._curveLayer = new GraphicsLayer({ id, title });
        this.mapView?.map.add(this._curveLayer);
      }
    }

    return this._curveLayer;
  }

  public getContourIntersects(original) {
    const userPoints = original ? this.userOriginalPoints : this.userActivePoints;
    const designedContours = this.mapView?.map.findLayerById(AppConstants.DesignedContourLayerId);
    const originalContours = this.mapView?.map.findLayerById(AppConstants.OriginalContourLayerId);

    let contours;
    // original parameter only exists if there is both design and original surface
    if (original) {
      contours = originalContours;
    } else if (!original && !designedContours) {
      contours = originalContours;
    } else {
      contours = designedContours;
    }

    const allPoints: any[] = [];

    // process contours for each vertex pair
    for (let i = 0; i < userPoints.length - 1; i++) {
      const data = this.prepareIntersectsData(userPoints, i);
      allPoints.push(userPoints[i]);

      const intersectingFeatures = contours.source.items.filter(function (feature) {
        return esriGeometryEngine.intersects(feature.geometry, data.line);
      });

      // process each intersecting contour
      const intersections: any[] = [];
      intersectingFeatures.forEach((contour) => {
        const cut = esriGeometryEngine.cut(data.line, contour.geometry) as any;
        if (cut) {
          for (const path of cut[0].paths) {
            for (const point of path) {
              // omit intersections that match start or end points
              if ((point[0] !== data.projectedStartPoint.x) && (point[1] !== data.projectedStartPoint.x) && (point[0] !== data.projectedEndPoint.x) && (point[1] !== data.projectedEndPoint.x)) {
                const newXyzPoint = new XyzPoint(point[0], point[1], contour.attributes.elevation);
                const distance = getDistance2D(newXyzPoint, data.startPoint);
                const intersection = {
                  point: new Point({ x: point[0], y: point[1], z: contour.attributes.elevation, spatialReference: this.mapView!.spatialReference }),
                  distance
                }
                intersections.push(intersection);
              }
            }
          }
        }
      })

      // order intersections by distance from start point
      intersections.sort((a, b) => {
        return a.distance - b.distance;
      });
      intersections.forEach((item) => {
        const intersectingPoint = esriProjection.project(item.point as Geometry, this.dataSpatialReference!) as Point;
        const intersectingXyzPoint = new XyzPoint(intersectingPoint.x, intersectingPoint.y, intersectingPoint.z);
        allPoints.push(intersectingXyzPoint);
      })
      if (i === (userPoints.length - 2)) allPoints.push(userPoints[i + 1]);
    }
    if (original) {
      this.originalProfilePoints = allPoints;
    } else {
      this.activeProfilePoints = allPoints;
    }
  }

  protected prepareIntersectsData(userPoints, i) {
    const startMapPoint = new Point({ x: userPoints[i].x, y: userPoints[i].y, z: userPoints[i].z, spatialReference: this.dataSpatialReference });
    const endMapPoint = new Point({ x: userPoints[i + 1].x, y: userPoints[i + 1].y, z: userPoints[i + 1].z, spatialReference: this.dataSpatialReference });

    const projectedStartPoint = esriProjection.project(startMapPoint as Geometry, this.mapView!.spatialReference) as Point;
    const projectedEndPoint = esriProjection.project(endMapPoint as Geometry, this.mapView!.spatialReference) as Point;

    const startPoint = new XyzPoint(projectedStartPoint.x, projectedStartPoint.y, projectedStartPoint.z);
    const endPoint = new XyzPoint(projectedEndPoint.x, projectedEndPoint.y, projectedEndPoint.z);

    const line = new Polyline({
      paths: [[[startPoint.x, startPoint.y], [endPoint.x, endPoint.y]]],
      spatialReference: this.mapView?.spatialReference
    });

    const data = { projectedStartPoint, projectedEndPoint, startPoint, line }
    return data
  }

  abstract moreSketchVmSetup();

  abstract SketchPolyline();

  abstract toggleAlignmentSnapping();

  abstract SketchPoint(applyDefaultCurves?);

  abstract ReshapePolyline(applyDefaultCurves?);

  abstract LoadEditToMap(data: any, keyCode: ProfileKeyCode);

  abstract drawCurve(curveData: any);

  abstract removeMapLayer(layer: DynamicProfileLayerType);

  abstract configSketchVM();

  abstract handlePolylineCreateEvent(event: __esri.SketchViewModelCreateEvent): Promise<void>;

  abstract handlePointCreateEvent(event: __esri.SketchViewModelCreateEvent): Promise<void>;

  abstract handleReshapeEvent(event: __esri.SketchViewModelUpdateEvent): Promise<void>;

  abstract updateAlignmentOnMap(data: any, vertexIndex);

  abstract checkToConvertAlignment(index, fromDeletion);

  abstract redrawAlignmentProfile();

  abstract generateAlignmentLabelsLayer();

  abstract setAlignment(data: any);
  
  abstract clearAlignment();

  abstract addStations();

  abstract setTracker(d: number, y: number);
  
  abstract clearTracker();

  abstract clearReachStations();


  completeSketch() {
    this.mainSketchVM?.complete();
  }

  @action
  private setUpSketchCapability(): void {
    const map = this.mapView?.map;
    if (map) {
      const pntLyr = map.findLayerById(AppConstants.SurveyPointsLayerId);
      if (pntLyr) pntLyr.visible = false;

      const originalTinLyr = map.findLayerById(AppConstants.OriginalTinTrianglesLayerId);
      if (originalTinLyr) originalTinLyr.visible = false;

      const designedTinLyr = map.findLayerById(AppConstants.DesignedTinTrianglesLayerId);
      if (designedTinLyr) designedTinLyr.visible = false;
      // console.log('new SketchViewModel for dynamic profile MapStore');
      this.mainSketchVM = new SketchViewModel({
        layer: this.sketchLayer,
        view: this.mapView!,
      });

      this.configSketchVM();

      this.mainSketchVM.on('create', (event) => this.handleMainSketchVmCreateEvent(event));
      this.mainSketchVM.on('update', (event) => this.handleMainSketchVmUpdateEvent(event));
      this.mainSketchVM.on('undo', (event) => this.handleMainSketchVmUndoEvent(event));

      // method template
      this.moreSketchVmSetup();
    }
  }

  private handleMainSketchVmUndoEvent(event: __esri.SketchViewModelUndoEvent): void { }

  private handleMainSketchVmUpdateEvent(event: __esri.SketchViewModelUpdateEvent): void {
    if (event.tool === 'reshape') {
      this.handleReshapeEvent(event);
    }
  }

  private handleMainSketchVmCreateEvent(event: __esri.SketchViewModelCreateEvent): void {
    if (event.tool === 'polyline') {
      this.handlePolylineCreateEvent(event);
    } else if (event.tool === 'point') {
      this.handlePointCreateEvent(event);
    }
  }

  // communication pattern 1: broadcast/observer pattern
  @action
  protected broadcastNewVertexInfo(
    newVertex: XyzPoint,
    state: string,
    vertexIndex: number,
    z: number,
    x: number,
    y: number,
    keyCode: ProfileKeyCode,
    blockTable: boolean = false,
    blockProfileForTable: boolean = false,
    blockProfile2dAlignment: boolean = false,
    originalVertex?: XyzPoint,
  ): void {
    this.hub?.NextNewVertexInfo({ vertex: newVertex, state, vertexIndex, z, x, y, keyCode, originalVertex, blockProfileForTable, blockProfile2dAlignment, blockTable });
  }

  @action
  protected broadcastInsertVertexInfo(newVertex: XyzPoint, vertexIndex: number, keyCode: ProfileKeyCode, blockModifyProfile: boolean = false, alignmentObject?): void {
    this.hub?.NextInsertVertexInfo({ newVertex, vertexIndex, keyCode, blockModifyProfile, alignmentObject });
  }

  @action
  protected broadcastMoveVertexInfo(newVertex: XyzPoint, vertexIndex: number, keyCode: ProfileKeyCode, blockModifyProfile: boolean, alignmentObject): void {
    this.hub?.NextMoveVertexInfo({ newVertex, vertexIndex, keyCode, blockModifyProfile, alignmentObject });
  }

  @action
  protected broadcastSketchState(state): void {
    this.hub?.NextMapSketchState(state);
  }

  protected startLengthyProfileProcess(state: boolean) {
    this.hub?.startLengthyProfileProcess(state)
  }

  protected removeCurve() {
    if (this._curveLayer) {
      this.mapView?.map?.remove(this._curveLayer);
      this._curveLayer.destroy();
      this._curveLayer = void 0;
    }
  }

  protected reomvoePrimary() {
    if (this._sketchLayer) {
      this.mapView?.map?.remove(this._sketchLayer);
      this._sketchLayer.destroy();
      this._sketchLayer = void 0;
    }
  }

  protected removeSketchGraphic() {
    if (this._sketchLayer) {
      this._sketchLayer.removeAll();
    }
    if (this._curveLayer) {
      this._curveLayer.removeAll();
    }
  }

  protected interpolateCoords(distance: number, lookupDistance: number[], lookupXY: number[][]): number[] {
    let iLow = 0;
    let iHigh = lookupDistance.length - 1;

    // Handle out-of-range
    if (distance <= lookupDistance[iLow]) {
      return [lookupXY[iLow][0], lookupXY[iLow][1]];
    }
    if (distance >= lookupDistance[iHigh]) {
      return [lookupXY[iHigh][0], lookupXY[iHigh][1]];
    }

    // Return if exact match
    const exactIndex = lookupDistance.indexOf(distance);
    if (exactIndex !== -1) {
      return [lookupXY[exactIndex][0], lookupXY[exactIndex][1]];
    }

    // Binary search to find correct interval
    while (iHigh - iLow > 1) {
      const iMid = Math.floor((iHigh + iLow) / 2);
      if (distance < lookupDistance[iMid]) {
        iHigh = iMid;
      } else {
        iLow = iMid;
      }
    }

    // Interpolate between iLow and iHigh
    return this.interpolateXY(iLow, iHigh, distance, lookupDistance, lookupXY);
  }

  protected interpolateXY(iLow: number, iHigh: number, distance: number, lookupDistance: number[], lookupXY: number[][]): number[] {
    const factor = (lookupDistance[iHigh] - distance) / (lookupDistance[iHigh] - lookupDistance[iLow]);
    return [
      lookupXY[iHigh][0] - (lookupXY[iHigh][0] - lookupXY[iLow][0]) * factor,
      lookupXY[iHigh][1] - (lookupXY[iHigh][1] - lookupXY[iLow][1]) * factor
    ];
  }

  cleanUp() {
    this.reomvoePrimary();
    this.removeCurve();
  }

  Dispose() {
    // DON'T dispose mapView!
    // console.log('MapView highlight resumed.');
    this._highlightSuspender?.stop();

    if (this.deleteVertexReactionDisposer) this.deleteVertexReactionDisposer();

    // console.log('Dynamic main sketchVM destroyed');
    this.mainSketchVM?.destroy();

    this._mapTooltipProvider?.Dispose();
  }
}

export { DynamicMapStoreBase };
